/**
 * @file WithWMSOverview.jsx
 * @description 창고관리시스템(WMS) 소개 페이지입니다.
 */

import {
  ArrowRight,
  Barcode,
  Boxes,
  Database,
  MapPin,
  Settings,
  Target,
  Truck,
} from 'lucide-react';
import { solWms } from '../../assets';
import { solutionItems } from '../../constants/solutionData';

const WMS_DATA = solutionItems.find((item) => item.id === 'wms');
const SUMMARY_ICONS = [Target, Settings, Database];

const CAPABILITY_META = [
  { category: 'RECEIVING', Icon: Barcode },
  { category: 'LOCATION TRACE', Icon: MapPin },
  { category: 'ISSUE & RETURN', Icon: Truck },
  { category: 'STOCK COUNT', Icon: Boxes },
];

const CAPABILITIES = WMS_DATA.keyModules.map((module, index) => ({
  num: String(index + 1).padStart(2, '0'),
  category: CAPABILITY_META[index].category,
  title: module.label,
  Icon: CAPABILITY_META[index].Icon,
  desc: module.desc ?? WMS_DATA.features[index],
}));

const PROCESS_STEPS = [
  { n: '01', title: '입고 등록', desc: '품목, Lot, 수량, 검수 정보를 입고 기준으로 등록' },
  { n: '02', title: '위치 지정', desc: '보관 조건과 창고 위치를 자재 정보와 연결' },
  { n: '03', title: '재고 이동', desc: '이동, 출고, 반납 이력을 바코드 기준으로 기록' },
  { n: '04', title: '실사 확인', desc: '실물 재고와 시스템 재고를 비교하고 조정' },
  { n: '05', title: '라벨·리포트', desc: '라벨 발행과 재고 리포트로 추적성 확보' },
];

function WMSHero() {
  return (
    <section className="rws-hero">
      <div className="container">
        <div className="rws-hero-inner">
          <div className="rws-hero-visual">
            <img src={solWms} alt="WMS 창고관리 시스템 화면 예시" className="rws-hero-image" />
          </div>

          <div className="rws-hero-text">
            <p className="rws-eyebrow">{WMS_DATA.badge}</p>
            <h1 className="rws-hero-title">
              {WMS_DATA.portfolioTitle[0]}
              <br />
              <em className="rws-accent">{WMS_DATA.portfolioTitle[1]}</em>
            </h1>
            <p className="rws-hero-desc">{WMS_DATA.summary}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CapabilityCard({ item }) {
  const Icon = item.Icon;

  return (
    <article className="rws-cap-card">
      <span className="rws-cap-num">{item.num}</span>
      <Icon size={28} color="var(--rws-accent)" strokeWidth={1.8} aria-hidden="true" />
      <p className="rws-cap-category">{item.category}</p>
      <h3 className="rws-cap-title">{item.title}</h3>
      <p className="rws-cap-desc">{item.desc}</p>
    </article>
  );
}

function WMSCapabilities() {
  return (
    <section className="rws-caps">
      <div className="container">
        <div className="rws-caps-header">
          <p className="rws-eyebrow">CORE CAPABILITIES</p>
          <h2 className="rws-caps-title">
            {WMS_DATA.title}의 <em className="rws-accent">핵심 모듈</em>
          </h2>
          <p className="rws-caps-intro">{WMS_DATA.portfolioDesc}</p>
        </div>

        <div className="rws-caps-grid">
          {CAPABILITIES.map((cap) => (
            <CapabilityCard key={cap.num} item={cap} />
          ))}
        </div>
      </div>
    </section>
  );
}

function WMSProcessSection() {
  return (
    <section className="rws-proposal rws-proposal-process wms-process-section">
      <div className="container">
        <div className="rws-process-head">
          <p className="rws-eyebrow">STANDARD WAREHOUSE FLOW</p>
          <h2 className="rws-proposal-title">
            입고부터 <em className="rws-accent">출고 추적까지</em>
          </h2>
          <p className="rws-proposal-desc">{WMS_DATA.portfolioDesc}</p>
        </div>

        <div className="rws-process-steps">
          <div className="rws-process-line" aria-hidden="true" />
          {PROCESS_STEPS.map((step) => (
            <article key={step.n} className="rws-process-step">
              <span className="rws-process-num">{step.n}</span>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </article>
          ))}
        </div>

        <div className="rws-before-after">
          <div className="rws-compare-col">
            <p className="rws-compare-label">Before</p>
            <h3>위치와 Lot 추적이 분리된 창고 운영</h3>
            <ul>
              <li>입출고와 보관 위치 변경 이력 분산</li>
              <li>Lot·사용기한 추적 확인 지연</li>
              <li>실사와 라벨 발행 업무의 반복 처리</li>
            </ul>
          </div>
          <div className="rws-compare-arrow" aria-hidden="true"><ArrowRight size={24} /></div>
          <div className="rws-compare-col is-after">
            <p className="rws-compare-label">After</p>
            <h3>바코드 중심의 재고 흐름 관리</h3>
            <ul>
              <li>입고·위치·이동·출고 이력 통합</li>
              <li>Lot, 사용기한, 보관 위치 추적 강화</li>
              <li>재고 정확도와 창고 운영 표준화 향상</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function WMSFinalInfo() {
  return (
    <section className="rws-final-info">
      <div className="container">
        <div className="rws-final-head">
          <p className="rws-eyebrow">WAREHOUSE MANAGEMENT SUMMARY</p>
          <h2 className="rws-final-title">
            창고 운영의 <em className="rws-accent">추적 기준</em>을 한 번에 정리합니다.
          </h2>
        </div>
        <div className="sol-info-grid fade-in rws-info-grid">
          <div className="sol-summary-stack">
            {WMS_DATA.highlights.map((item, index) => {
              const Icon = SUMMARY_ICONS[index] ?? Target;
              return (
                <article key={item.label} className="sol-summary-card">
                  <div className="sol-summary-icon" aria-hidden="true"><Icon size={30} strokeWidth={1.8} /></div>
                  <div><h4>{item.label}</h4><p>{item.text}</p></div>
                </article>
              );
            })}
          </div>
          <article className="sol-list-card"><h3 className="sol-list-card-title">주요 기능</h3><ul className="sol-card-list">{WMS_DATA.features.map((item) => <li key={item}>{item}</li>)}</ul></article>
          <article className="sol-list-card"><h3 className="sol-list-card-title">도입 효과</h3><ul className="sol-card-list">{WMS_DATA.effects.map((item) => <li key={item}>{item}</li>)}</ul></article>
        </div>
      </div>
    </section>
  );
}

export default function WithWMSOverview() {
  return (
    <div className="rws-solution-theme wms-solution-theme">
      <WMSHero />
      <WMSCapabilities />
      <WMSProcessSection />
      <WMSFinalInfo />
    </div>
  );
}
